{
  const axiosErrorOf = (error: unknown): RollbarAxiosError | undefined => {
    let current = error as RollbarAxiosError | null | undefined
    while (current != null && !current.isAxiosError) current = current.cause as RollbarAxiosError | null | undefined
    return current ?? undefined
  }

  /**
   * Checks whether a frame is from one of our own scripts
   * (which will have a filename ending in .js or .mjs)
   * or is from an inline script, which will have a 'filename'
   * that's just the URL of the page.
   *
   * @returns `true` when the filename's path ends in `.js` or
   *   `.mjs`, or when the filename is not a parseable URL (ie err on the side of reporting).
   */
  const isFromOurScriptFile = (frame: RollbarFrame): boolean => {
    try {
      return /\.m?js$/.test(new URL(frame.filename).pathname)
    } catch {
      return true
    }
  }

  /**
   * This blocks exceptions thrown by Google Translate in
   * Google Chrome on iOS (and the Google app on iOS).
   *
   * @returns `true` when the item is almost certainly a stack overflow from
   *   Google Translate and Rollbar should ignore it.
   */
  const isStackOverflowFromiOSChromeTranslate = (item: RollbarItem | undefined): boolean => {
    const body = item?.data?.body
    const trace = body?.trace ?? body?.trace_chain?.[0]
    const exception = trace?.exception
    if (exception?.class !== "RangeError") return false
    if (!/Maximum call stack size exceeded/.test(exception.message ?? "")) return false

    // Confirm that the user agent is Chrome on iOS (CriOS) or
    // the Google iOS app (GSA)
    if (!/\b(CriOS|GSA)\//.test(navigator.userAgent)) return false

    const pageClasses = document.documentElement?.classList
    const pageIsGoogleTranslated = pageClasses?.contains("translated-ltr") || pageClasses?.contains("translated-rtl")
    if (!pageIsGoogleTranslated) return false

    const frames = trace?.frames ?? []
    return frames.length > 0 && !frames.some(isFromOurScriptFile)
  }

  const moduleLoadFailures: Array<{ pattern: RegExp; key: string; label: string }> = [
    { pattern: /Importing module .* is not found/, key: "mod-not-found", label: "Importing module … is not found" },
    {
      pattern: /Importing binding name .* is not found/,
      key: "binding-not-found",
      label: "Importing binding … is not found",
    },
    {
      pattern: /Importing a module script failed/,
      key: "mod-script-failed",
      label: "Importing a module script failed",
    },
    { pattern: /doesn't provide an export named/, key: "no-export-ff", label: "module doesn't provide an export" },
    { pattern: /does not provide an export named/, key: "no-export-cr", label: "module does not provide an export" },
    {
      pattern: /error loading dynamically imported module/,
      key: "dyn-import-ff",
      label: "error loading dynamically imported module",
    },
    {
      pattern: /Failed to fetch dynamically imported module/,
      key: "dyn-import-cr",
      label: "Failed to fetch dynamically imported module",
    },
  ]

  const hashedChunkFilename = /([^/'"\s]+?)-[A-Za-z0-9_-]{8}\.m?js\b/

  const moduleLoadBurstMs = 10_000
  let moduleLoadBurst: { startedAt: number; fingerprint: string } | undefined

  const messagesOf = (payload: RollbarPayload): Array<string> => {
    const body = payload.body
    const traces = [body?.trace, ...(Array.isArray(body?.trace_chain) ? body.trace_chain : [])]
    const candidates = traces.flatMap((trace) => {
      const exception = trace?.exception
      return [exception?.message, exception?.description, exception?.raw]
    })
    return [...candidates, body?.message?.body].filter((message): message is string => typeof message === "string")
  }

  window.__rollbarFilters = {
    ignoredMessages: [
      "ResizeObserver loop completed with undelivered notifications.",
      /Unable to preload CSS/i,
      // Globals that Firefox for iOS, crypto-wallet browsers and iOS in-app browsers inject; our code never uses them.
      /__firefox__/,
      /window\.ethereum/,
      /window\.webkit\.messageHandlers/,
    ],
    // Our clients set no timeout, so ECONNABORTED is the browser abandoning the request, like ERR_CANCELED.
    // 401, 403 and 429 are auth and rate limiting doing their job.
    checkIgnore(_isUncaught, args, item) {
      const failure = axiosErrorOf(Array.from(args ?? []).find((arg) => arg instanceof Error))
      return (
        isStackOverflowFromiOSChromeTranslate(item) ||
        ["ERR_CANCELED", "ECONNABORTED"].includes(failure?.code ?? "") ||
        [401, 403, 429].includes(failure?.response?.status ?? 0)
      )
    },
    // 400, 404, 405, 406 and 415 mean our client and server disagree, so they stay errors.
    transform(payload, item) {
      const failure = axiosErrorOf(item?.err)
      const status = failure?.response?.status
      const clientError = status != null && status >= 400 && status < 500 && ![400, 404, 405, 406, 415].includes(status)
      if (failure?.code === "ERR_NETWORK" || clientError) payload.level = "warning"

      const extra = payload.body?.trace?.extra ?? payload.body?.message?.extra
      if (extra?.customJs) {
        payload.custom ??= {}
        payload.custom.customJs = extra.customJs
        payload.custom.storeId = extra.storeId
        return
      }

      const messages = messagesOf(payload)
      const moduleLoad = moduleLoadFailures.find(({ pattern }) => messages.some((message) => pattern.test(message)))
      if (!moduleLoad) return
      const chunk = messages.map((message) => hashedChunkFilename.exec(message)?.[1]).find(Boolean)
      const fingerprint = chunk ? `ml:${moduleLoad.key}:${chunk}` : `ml:${moduleLoad.key}`
      payload.fingerprint = fingerprint
      payload.title = `Browser module load failed: ${moduleLoad.label}${chunk ? ` (${chunk})` : ""}`

      const now = Date.now()
      if (moduleLoadBurst != null && now - moduleLoadBurst.startedAt < moduleLoadBurstMs) {
        payload.level = "warning"
        payload.custom ??= {}
        payload.custom.moduleLoadBurstOf = moduleLoadBurst.fingerprint
      } else {
        moduleLoadBurst = { startedAt: now, fingerprint }
      }
    },
  }
}

type RollbarAxiosError = {
  isAxiosError?: boolean
  code?: string
  response?: { status: number }
  cause?: unknown
}

type RollbarFrame = { filename: string }

type RollbarTrace = {
  exception?: { class?: string; message?: string }
  frames?: Array<RollbarFrame>
}

type RollbarItem = {
  data?: { body?: { trace?: RollbarTrace; trace_chain?: Array<RollbarTrace> } }
}

type RollbarPayloadException = { message?: unknown; description?: unknown; raw?: unknown }

type RollbarPayload = {
  level?: string
  fingerprint?: string
  title?: string
  body?: {
    trace?: { extra?: Record<string, unknown>; exception?: RollbarPayloadException }
    trace_chain?: Array<{ exception?: RollbarPayloadException }>
    message?: { body?: unknown; extra?: Record<string, unknown> }
  }
  custom?: Record<string, unknown>
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- merges into the global Window; this file is a classic script
interface Window {
  __rollbarFilters: {
    ignoredMessages: Array<string | RegExp>
    checkIgnore: (isUncaught: boolean, args: ArrayLike<unknown> | undefined, item?: RollbarItem) => boolean
    transform: (payload: RollbarPayload, item?: { err?: unknown }) => void
  }
}
